import { AttestationApi } from '@/api';
import {
  attestationLoading,
  fetchApplicationSummarySuccess,
  fetchAttestationFailure,
  fetchAttestationSuccess,
  fetchAttestationSummarySuccess,
} from '../slices';
import { AppDispatch } from '../store';
import {
  adaptGetAttestationResponseToAttestation,
  adaptGetAttestationSummaryResponseToApplicationSummary,
  adaptGetSummaryResponseToAttestationSummary,
  adaptFormItemArrayResponseToFormFields,
} from '@/adapters';
import { updateFieldFailedAlert } from '@/services/alert';
import { fetchAttestationFormSuccess } from '@/forms/store';
import {
  UpdateAttestationConditionalFieldsGroupRequestDTO,
  UpdateAttestationRequestDTO,
} from '@/api/attestation/DTOs';
import type { AppError } from '@/types/errors.interface';
import { toStoreError } from '@/types/storeError.interface';
import {
  notifyFetched,
  tookEffectDespite,
  type GroupActionOutcome,
  type OnFormFetched,
} from './groupActionOutcome';

/** Options accepted by fetches that can run behind an already-rendered attestation. */
export type AttestationFetchOptions = {
  background?: boolean;
  onFetched?: OnFormFetched;
};

export const fetchAttestation =
  (applicationId: number, options: AttestationFetchOptions = {}) =>
  async (dispatch: AppDispatch) => {
    // Background refreshes deliberately leave `loading` alone: flipping it swaps the
    // form for a skeleton, which remounts DynamicForm and resets the reinitialize counter.
    if (!options.background) {
      dispatch(attestationLoading());
    }

    try {
      const result = await new AttestationApi().getAttestation({
        applicationId,
      });

      if (result.success) {
        const formFields = adaptFormItemArrayResponseToFormFields(
          result.data.items,
        );
        notifyFetched(options.onFetched, formFields);
        dispatch(
          fetchAttestationSuccess({
            attestations: {
              [applicationId]: adaptGetAttestationResponseToAttestation(
                result.data,
              ),
            },
            background: options.background,
          }),
        );

        dispatch(fetchAttestationFormSuccess({ [applicationId]: formFields }));
      } else {
        notifyFetched(options.onFetched, undefined);
        dispatch(fetchAttestationFailure(toStoreError(result.error)));
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

export const updateAttestation =
  (
    applicationId: number,
    attestationField: UpdateAttestationRequestDTO['attestationField'],
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      const result = await new AttestationApi().updateAttestation({
        applicationId,
        attestationField,
      });

      if (result.success) {
        if (!result.data.success) {
          updateFieldFailedAlert();
          dispatch(
            fetchAttestationFailure({
              code: 'UNK_001',
              message: `Failed to update attestation field ${attestationField.itemID}`,
              category: 'server',
            }),
          );
        } else {
          return result.data.reloadRequired;
        }
      } else {
        dispatch(fetchAttestationFailure(toStoreError(result.error)));
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

export const fetchAttestationSummary =
  (applicationId: number) => async (dispatch: AppDispatch) => {
    // No loading dispatch: this only refreshes progress counters, and `loading` is what
    // QuestionsPage swaps the form out for a skeleton on.
    try {
      const result = await new AttestationApi().getSummary({ applicationId });

      if (result.success) {
        dispatch(
          fetchAttestationSummarySuccess({
            applicationId,
            summary: adaptGetSummaryResponseToAttestationSummary(
              result.data.attestation,
            ),
          }),
        );

        dispatch(
          fetchApplicationSummarySuccess({
            id: applicationId,
            ...adaptGetAttestationSummaryResponseToApplicationSummary(
              result.data,
            ),
          }),
        );
      } else {
        dispatch(fetchAttestationFailure(toStoreError(result.error)));
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

export const updateAttestationConditionalFieldsGroup =
  (
    applicationId: number,
    formField: UpdateAttestationConditionalFieldsGroupRequestDTO['formField'],
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      const result =
        await new AttestationApi().updateAttestationConditionalFieldsGroup({
          applicationId,
          formField,
        });

      if (result.success) {
        if (!result.data.success) {
          updateFieldFailedAlert();
          dispatch(
            fetchAttestationFailure({
              code: 'UNK_001',
              message: `Failed to update ConditionalFieldsGroup ${formField.groupID}`,
              category: 'server',
            }),
          );
        } else {
          return result.data.reloadRequired;
        }
      } else {
        dispatch(fetchAttestationFailure(toStoreError(result.error)));
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

export const addAttestationGroup =
  (applicationId: number, groupId: string) => async (dispatch: AppDispatch) => {
    try {
      const result = await new AttestationApi().addAttestationGroup({
        applicationId,
        groupId,
      });

      if (result.success) {
        dispatch(fetchAttestation(applicationId));
      } else {
        dispatch(fetchAttestationFailure(toStoreError(result.error)));
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

/** Removes a group entry, reporting to `outcome` whether the server took it. */
export const removeAttestationGroup =
  (applicationId: number, groupId: string, outcome?: GroupActionOutcome) =>
  async (dispatch: AppDispatch) => {
    let accepted = false;
    let failure: AppError | undefined;
    try {
      const result = await new AttestationApi().removeAttestationGroup({
        applicationId,
        groupId,
      });

      if (!result.success) {
        failure = result.error;
      } else if (result.data !== true) {
        // False when the server found nothing to remove, and so removed nothing.
        updateFieldFailedAlert();
        dispatch(
          fetchAttestationFailure({
            code: 'UNK_001',
            message: `Failed to remove attestation group ${groupId}`,
            category: 'server',
          }),
        );
      } else {
        accepted = true;
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }

    // Outside the try, as in the section and missing-item thunks: a throw there is no failed request.
    if (failure) return settleFailure(failure, dispatch, applicationId, outcome);
    if (!accepted) return;
    outcome?.onAccepted();
    dispatch(fetchAttestation(applicationId));
  };

/** Clears a group entry, reporting to `outcome` whether the server took it. */
export const clearAttestationGroup =
  (applicationId: number, groupId: string, outcome?: GroupActionOutcome) =>
  async (dispatch: AppDispatch) => {
    let accepted = false;
    let failure: AppError | undefined;
    try {
      const result = await new AttestationApi().clearAttestationGroup({
        applicationId,
        groupId,
      });

      if (result.success) {
        if (!result.data.success) {
          updateFieldFailedAlert();
          dispatch(
            fetchAttestationFailure({
              code: 'UNK_001',
              message: `Failed to clear attestation group ${groupId}`,
              category: 'server',
            }),
          );
        } else {
          accepted = true;
        }
      } else {
        failure = result.error;
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchAttestationFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }

    // Outside the try, as in the section and missing-item thunks: a throw there is no failed request.
    if (failure) return settleFailure(failure, dispatch, applicationId, outcome);
    if (!accepted) return;
    outcome?.onAccepted();
    dispatch(fetchAttestation(applicationId));
  };

/** Reports a failed remove or clear, unless the refetch after a lost answer shows it taken. */
const settleFailure = async (
  failure: AppError,
  dispatch: AppDispatch,
  applicationId: number,
  outcome: GroupActionOutcome | undefined,
) => {
  const refetch = (onFetched: OnFormFetched) =>
    fetchAttestation(applicationId, { onFetched });
  if (await tookEffectDespite(failure, dispatch, refetch, outcome)) return;
  dispatch(fetchAttestationFailure(toStoreError(failure)));
};
